/**
 * Markdown round-trip configuration for our custom Tiptap nodes & marks.
 *
 * tiptap-markdown reads each node/mark's `addStorage().markdown` spec for
 * serialization and lets us extend the markdown-it instance via
 * `parse.setup`. We attach those specs by `extend()`-ing the existing
 * extensions so the editor.ts wiring stays small.
 *
 * Mappings (see plan in /memories/session/plan.md):
 *   Highlight (<mark>)  <-> ==text==
 *   Callout             <-> > [!NOTE] / [!TIP] / [!IMPORTANT] / [!WARNING] / [!CAUTION]
 *   Youtube / Bilibili  <-> :::embed { provider="..." id|bvid="..." ... }\n:::
 */

import Highlight from '@tiptap/extension-highlight';
import { Youtube } from '@tiptap/extension-youtube';
import { Table } from '@tiptap/extension-table';
import { getHTMLFromFragment } from '@tiptap/core';
import { Fragment } from '@tiptap/pm/model';
import Callout from './tiptap/callout';
import { Bilibili } from './tiptap/bilibili';
// @ts-ignore — no types ship with markdown-it-container
import container from 'markdown-it-container';

// ---------------------------------------------------------------------------
// Callout <-> GFM Alert
// ---------------------------------------------------------------------------

// internal callout type -> GFM alert keyword
const CALLOUT_TO_ALERT: Record<string, string> = {
  note: 'NOTE',
  check: 'TIP',
  caution: 'CAUTION',
  warning: 'WARNING',
};
const ALERT_TO_CALLOUT: Record<string, string> = Object.fromEntries(
  Object.entries(CALLOUT_TO_ALERT).map(([k, v]) => [v, k]),
);
// Be lenient: also accept GFM IMPORTANT (no exact internal equivalent -> note).
ALERT_TO_CALLOUT['IMPORTANT'] = 'caution';

const ALERT_LINE_RE = /^\s*\[!([A-Z]+)\]\s*$/;

const CalloutMarkdown = Callout.extend({
  addStorage() {
    return {
      markdown: {
        serialize(state: any, node: any) {
          const alert = CALLOUT_TO_ALERT[node.attrs.type] || 'NOTE';
          state.write(`> [!${alert}]\n`);
          state.wrapBlock('> ', null, node, () => state.renderContent(node));
        },
        parse: {
          setup() {
            // No markdown-it rule needed — alerts are produced as
            // <blockquote><p>[!NOTE]\n...</p></blockquote> and rewritten in updateDOM.
          },
          updateDOM(element: HTMLElement) {
            element.querySelectorAll('blockquote').forEach((bq) => {
              const firstP = bq.querySelector(':scope > p:first-child');
              if (!firstP) return;

              // The first line of the first paragraph is the [!TYPE] marker.
              const text = firstP.firstChild;
              if (!text || text.nodeType !== Node.TEXT_NODE) return;
              const lines = (text.textContent || '').split('\n');
              const m = lines[0].match(/^\s*\[!([A-Z]+)\]\s*$/);
              if (!m) return;
              const internal = ALERT_TO_CALLOUT[m[1]];
              if (!internal) return;

              // Strip the marker line. If nothing else remains in that text node,
              // also drop the leading <br>/whitespace.
              if (lines.length > 1) {
                text.textContent = lines.slice(1).join('\n');
              } else {
                // Marker was the only text; remove it and any leading <br>.
                const next = text.nextSibling;
                text.remove();
                if (next && next.nodeName === 'BR') next.remove();
              }
              if (!firstP.textContent && firstP.children.length === 0) firstP.remove();

              const div = document.createElement('div');
              div.setAttribute('data-callout-type', internal);
              while (bq.firstChild) div.appendChild(bq.firstChild);
              bq.replaceWith(div);
            });
          },
        },
      },
    };
  },
});

// ---------------------------------------------------------------------------
// Highlight <-> ==text==
// ---------------------------------------------------------------------------

const HighlightMarkdown = Highlight.extend({
  addStorage() {
    return {
      markdown: {
        serialize: { open: '==', close: '==', mixable: true, expelEnclosingWhitespace: true },
        parse: {
          setup(md: any) {
            // Inline rule: ==text== -> <mark>text</mark>
            md.inline.ruler.after('emphasis', 'mark', (state: any, silent: boolean) => {
              const start = state.pos;
              if (state.src.charCodeAt(start) !== 0x3d /* = */) return false;
              if (state.src.charCodeAt(start + 1) !== 0x3d) return false;

              // find closing ==
              const max = state.posMax;
              let pos = start + 2;
              let found = -1;
              while (pos < max - 1) {
                if (state.src.charCodeAt(pos) === 0x3d && state.src.charCodeAt(pos + 1) === 0x3d) {
                  // require non-space immediately before/after content
                  if (pos > start + 2 && state.src.charCodeAt(pos - 1) !== 0x20) {
                    found = pos;
                    break;
                  }
                }
                pos += 1;
              }
              if (found < 0) return false;
              const content = state.src.slice(start + 2, found);
              if (!content || content.charCodeAt(0) === 0x20) return false;

              if (!silent) {
                const tokenOpen = state.push('mark_open', 'mark', 1);
                tokenOpen.markup = '==';
                const tokenText = state.push('text', '', 0);
                tokenText.content = content;
                const tokenClose = state.push('mark_close', 'mark', -1);
                tokenClose.markup = '==';
              }
              state.pos = found + 2;
              return true;
            });
          },
        },
      },
    };
  },
});

// ---------------------------------------------------------------------------
// :::embed { provider="..." ... } container
// ---------------------------------------------------------------------------

// Parses key="value" pairs from a container info string like
// `embed { provider="youtube" id="abc" title="X" }`.
function parseEmbedAttrs(info: string): Record<string, string> {
  const attrs: Record<string, string> = {};
  const m = info.match(/^embed\s*\{(.*)\}\s*$/);
  if (!m) return attrs;
  const re = /([a-zA-Z_][\w-]*)\s*=\s*"([^"]*)"/g;
  let match;
  while ((match = re.exec(m[1])) !== null) {
    attrs[match[1]] = match[2];
  }
  return attrs;
}

function escapeHtmlAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function setupEmbedContainer(md: any) {
  md.use(container, 'embed', {
    validate(params: string) {
      return /^embed\s*\{.*\}\s*$/.test(params.trim());
    },
    render(tokens: any[], idx: number) {
      if (tokens[idx].nesting !== 1) return '';
      const attrs = parseEmbedAttrs(tokens[idx].info.trim());
      switch (attrs.provider) {
        case 'youtube': {
          const id = attrs.id || '';
          if (!id) return '';
          let src = `https://www.youtube.com/embed/${encodeURIComponent(id)}`;
          if (/^\d+$/.test(attrs.start || '')) src += `?start=${attrs.start}`;
          // Wrap in div[data-youtube-video] so @tiptap/extension-youtube parseHTML matches.
          return `<div data-youtube-video><iframe src="${escapeHtmlAttr(src)}"></iframe></div>\n`;
        }
        case 'bilibili': {
          const bvid = attrs.bvid || '';
          if (!bvid) return '';
          const page = /^\d+$/.test(attrs.page || '') ? attrs.page : '1';
          const src = `https://player.bilibili.com/player.html?bvid=${encodeURIComponent(
            bvid,
          )}&page=${encodeURIComponent(page)}&autoplay=0`;
          return `<iframe src="${escapeHtmlAttr(src)}"></iframe>\n`;
        }
        default:
          return '';
      }
    },
  });
}

// Serializer helpers: build `:::embed { ... }\n:::` from node attrs.
function writeEmbedBlock(state: any, attrs: Record<string, string>) {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(attrs)) {
    if (v === undefined || v === null || v === '') continue;
    parts.push(`${k}="${String(v).replace(/"/g, '\\"')}"`);
  }
  state.write(`:::embed { ${parts.join(' ')} }\n:::`);
  state.closeBlock(state.node || undefined);
}

const YOUTUBE_RE = /(?:youtu\.be\/|youtube\.com\/(?:embed\/|watch\?v=|v\/|shorts\/))([\w-]{6,})/;

const YoutubeMarkdown = Youtube.extend({
  addStorage() {
    return {
      markdown: {
        serialize(state: any, node: any) {
          const m = (node.attrs.src || '').match(YOUTUBE_RE);
          const id = m ? m[1] : '';
          const attrs: Record<string, string> = { provider: 'youtube' };
          if (id) attrs.id = id;
          if (node.attrs.start) attrs.start = String(node.attrs.start);
          if (node.attrs.src) attrs.url = node.attrs.src;
          state.write(`:::embed { ${objectToAttrs(attrs)} }\n:::`);
          state.closeBlock(node);
        },
        parse: {
          setup(md: any) {
            setupEmbedContainer(md);
          },
        },
      },
    };
  },
});

const BILIBILI_RE = /bvid=([\w]+)/;

const BilibiliMarkdown = Bilibili.extend({
  addStorage() {
    return {
      markdown: {
        serialize(state: any, node: any) {
          const m = (node.attrs.src || '').match(BILIBILI_RE);
          const bvid = m ? m[1] : '';
          const attrs: Record<string, string> = { provider: 'bilibili' };
          if (bvid) attrs.bvid = bvid;
          if (node.attrs.src) attrs.url = node.attrs.src;
          state.write(`:::embed { ${objectToAttrs(attrs)} }\n:::`);
          state.closeBlock(node);
        },
        parse: {
          // setup is no-op here — Youtube extension already registered the container.
          setup() {},
        },
      },
    };
  },
});

function objectToAttrs(attrs: Record<string, string>): string {
  return Object.entries(attrs)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${k}="${String(v).replace(/"/g, '\\"')}"`)
    .join(' ');
}

// ---------------------------------------------------------------------------
// Table -> GFM markdown table
// ---------------------------------------------------------------------------
//
// `@tiptap/extension-table`'s Table node ships no markdown spec, so
// tiptap-markdown falls back to its `HTMLNode` serializer and emits the entire
// table as one minified raw-HTML blob — unreadable when readers fetch the `.md`
// view. We port tiptap-markdown's own GFM-table serializer here, with a
// graceful HTML fallback for tables that can't be expressed in pure markdown
// (rowspan/colspan, multi-block cells, or no header row).

function tableHasSpan(cell: any): boolean {
  return (cell.attrs?.colspan || 1) > 1 || (cell.attrs?.rowspan || 1) > 1;
}

// We emit GFM tables for anything that doesn't use rowspan/colspan. Missing
// header rows are synthesised; multi-block cells are flattened with <br>.
function isTableMarkdownSerializable(node: any): boolean {
  let ok = true;
  let hasRow = false;
  node.forEach((row: any) => {
    hasRow = true;
    row.forEach((cell: any) => {
      if (tableHasSpan(cell)) ok = false;
    });
  });
  return ok && hasRow;
}

// Render a single cell as inline markdown. Multi-block cells (e.g. two <p>s)
// are joined with a <br> so the row stays on one line, which is the only way
// GFM tables can express line breaks inside a cell.
function renderTableCell(state: any, cell: any) {
  const blocks: any[] = [];
  cell.forEach((child: any) => blocks.push(child));
  blocks.forEach((block, idx) => {
    if (idx > 0) state.write('<br>');
    if (block.isTextblock) {
      state.renderInline(block);
    } else {
      // Nested block (list, blockquote, …): fall back to text content so the
      // row doesn't collapse the table.
      state.text(block.textContent.replace(/\n+/g, ' '), false);
    }
  });
}

const TableMarkdown = Table.extend({
  addStorage() {
    return {
      markdown: {
        serialize(state: any, node: any, parent: any) {
          if (!isTableMarkdownSerializable(node)) {
            // Last-resort fallback for merged cells (rowspan/colspan): emit
            // raw HTML so no data is lost.
            const schema = node.type.schema;
            const html = getHTMLFromFragment(Fragment.from(node), schema);
            state.write(html);
            state.closeBlock(node);
            return;
          }

          const rows: any[] = [];
          node.forEach((row: any) => rows.push(row));
          if (rows.length === 0) return;

          const colCount = rows.reduce((max, r) => Math.max(max, r.childCount), 0);
          const firstRowIsHeader: boolean = (() => {
            let allHeader = true;
            rows[0].forEach((cell: any) => {
              if (cell.type.name !== 'tableHeader') allHeader = false;
            });
            return allHeader;
          })();

          state.inTable = true;

          const writeRow = (row: any, opts: { boldRowHeaders?: boolean } = {}) => {
            state.write('| ');
            let written = 0;
            row.forEach((cell: any, _p: any, j: number) => {
              if (j) state.write(' | ');
              const bold = opts.boldRowHeaders && cell.type.name === 'tableHeader';
              if (bold) state.write('**');
              renderTableCell(state, cell);
              if (bold) state.write('**');
              written += 1;
            });
            // Pad short rows so the table stays rectangular.
            for (let k = written; k < colCount; k += 1) state.write(' | ');
            state.write(' |');
            state.ensureNewLine();
          };

          const writeDelimiter = () => {
            const delimiterRow = Array.from({ length: colCount })
              .map(() => '---')
              .join(' | ');
            state.write(`| ${delimiterRow} |`);
            state.ensureNewLine();
          };

          if (firstRowIsHeader) {
            writeRow(rows[0]);
            writeDelimiter();
            for (let i = 1; i < rows.length; i += 1) {
              writeRow(rows[i], { boldRowHeaders: true });
            }
          } else {
            // No header row in the source — GFM requires one, so promote the
            // first body row to the header instead of synthesising an empty
            // one (which renders as a blank strip).
            writeRow(rows[0], { boldRowHeaders: true });
            writeDelimiter();
            for (let i = 1; i < rows.length; i += 1) {
              writeRow(rows[i], { boldRowHeaders: true });
            }
          }

          state.closeBlock(node);
          state.inTable = false;
        },
      },
    };
  },
});

export {
  CalloutMarkdown,
  HighlightMarkdown,
  YoutubeMarkdown,
  BilibiliMarkdown,
  TableMarkdown,
  CALLOUT_TO_ALERT,
  ALERT_TO_CALLOUT,
};
